import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Campaign } from '../../services/campaign.service';
import { ConfirmActionModalComponent } from '../../shared/components/confirm-action-modal';

/**
 * Confirmation modal component for duplicating an existing Campaign.
 * Provides a simple UI to confirm duplication of a campaign and emits events for closing or confirming the action.
 * Key methods:
 *   - onClose(): emits the close event to request closing the modal.
 *   - onConfirmDuplicate(): emits the confirmDuplicate event with the campaign payload when a campaign is set.
 * No additional Angular lifecycle hooks are implemented on this component.
 * @component
 * @selector app-duplicate-campaign-modal
 * @example
 *   <app-duplicate-campaign-modal [campaign]="selectedCampaign" (close)="onModalClose()" (confirmDuplicate)="onDuplicate($event)"></app-duplicate-campaign-modal>
 * @input Campaign | null campaign - The campaign object to be duplicated; null when no campaign is selected.
 * @output EventEmitter<void> close - Emitted when the user cancels or closes the modal without duplicating.
 * @output EventEmitter<Campaign> confirmDuplicate - Emitted with the Campaign to duplicate when the user confirms the action.
 */
@Component({
  selector: 'app-duplicate-campaign-modal',
  standalone: true,
  imports: [ConfirmActionModalComponent],
  templateUrl: './duplicate-campaign-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duplicate-campaign-modal.component.scss'],
})
export class DuplicateCampaignModalComponent {
  @Input() campaign: Campaign | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() confirmDuplicate = new EventEmitter<Campaign>();

  onClose(): void {
    this.close.emit();
  }

  onConfirmDuplicate(): void {
    if (this.campaign) {
      this.confirmDuplicate.emit(this.campaign);
    }
  }
}
